import React, { useState, useEffect } from 'react';
import { CheckCircle, Clock, TrendingUp, FileText } from 'lucide-react';
import { Cell, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ResponsivePie } from '@nivo/pie';
import { toast } from 'sonner';
import ProblemDetailModal from '../components/ProblemDetailModal';
import { fetchProblems, updateProblem, deleteProblem } from '../utils/supabaseHelpers';
import { supabase } from '../supabaseClient';
import { formatSimpleDate } from '../utils/dateFormatter';
import '../animations.css';



const Overview = () => {
  const [problems, setProblems] = useState([]);
  const [selectedProblem, setSelectedProblem] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [problemStatusData, setProblemStatusData] = useState([]);

  useEffect(() => {
    loadProblems();
  }, []);

  // Check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      console.log('🔐 Current user:', user);
      if (!user) {
        console.log('⚠️ No authenticated user found');
      }
    };
    checkAuth();
  }, []);

  const loadProblems = async () => {
    try {
      setLoading(true);
      const data = await fetchProblems();
      setProblems(data);

      // --- NEW CHART LOGIC ---
      // 1. Initialize counters
      let notDecidedCount = 0;
      let inProgressCount = 0;
      let doneCount = 0;

      // 2. Process all fetched problems
      if (data) {
        data.forEach(problem => {
          switch (problem.status) {
            // --- Group 1: Done ---
            case 'Done':
            case 'Solved':
            case 'completed': // Catches 'completed'
              doneCount++;
              break;
            // --- Group 2: In Progress ---
            case 'In Progress':
            case 'pending': // Catches 'pending'
              inProgressCount++;
              break;
            // --- Group 3: Not Decided ---
            default: // Catches 'Not Decided', 'decided_', and any other value
              notDecidedCount++;
          }
        });
      }

      // 3. Create the clean data array
      const cleanData = [
        { id: 'not_decided', label: 'Not Decided', value: notDecidedCount, color: '#3b82f6' },
        { id: 'in_progress', label: 'In Progress', value: inProgressCount, color: '#f97316' },
        { id: 'done', label: 'Done', value: doneCount, color: '#22c55e' },
      ].filter(item => item.value > 0); // Only show slices that have a value

      // 4. Set the new data into our state
      setProblemStatusData(cleanData);
      // --- END OF NEW CHART LOGIC ---

    } catch (error) {
      console.error('❌ Error loading problems:', error);
      toast.error('Failed to load problems: ' + error.message);
    } finally {
      setLoading(false);
    }
  };



  const stats = {
    total: problems.length,
    solved: problems.filter(p => p.status === 'Done').length,
    inProgress: problems.filter(p => p.status === 'In Progress').length,
    notDecided: problems.filter(p => p.status === 'Not Decided').length
  };

  // Status Distribution Data is now processed in loadProblems() and stored in problemStatusData state

  // Priority Breakdown Data
  const priorityData = [
    { name: 'Critical', value: problems.filter(p => p.priority?.toLowerCase() === 'critical').length, color: '#ef4444' },
    { name: 'High', value: problems.filter(p => p.priority?.toLowerCase() === 'high').length, color: '#fb923c' },
    { name: 'Medium', value: problems.filter(p => p.priority?.toLowerCase() === 'medium').length, color: '#fbbf24' },
    { name: 'Low', value: problems.filter(p => p.priority?.toLowerCase() === 'low').length, color: '#9ca3af' }
  ];

  // Domain Distribution
  const domainStats = [
    { domain: 'Mechanical', count: problems.filter(p => p.domain?.toLowerCase() === 'mechanical').length },
    { domain: 'Software', count: problems.filter(p => p.domain?.toLowerCase() === 'software').length },
    { domain: 'Electronics', count: problems.filter(p => p.domain?.toLowerCase() === 'electronics').length },
    { domain: 'Management', count: problems.filter(p => p.domain?.toLowerCase() === 'management').length }
  ];

  // Recent Activity (6 most recent) - Sort by updated_at, fallback to created_at
  const recentProblems = [...problems]
    .sort((a, b) => {
      const aTime = new Date(a.updated_at || a.created_at).getTime();
      const bTime = new Date(b.updated_at || b.created_at).getTime();
      return bTime - aTime;
    })
    .slice(0, 6);

  // 30-Day Trend
  const getLast30Days = () => {
    const days = [];
    for (let i = 29; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      days.push(date.toISOString().split('T')[0]);
    }
    return days;
  };

  const trendData = getLast30Days().map(date => {
    const created = problems.filter(p => {
      // Handle different date field names and formats
      const reportDate = p.dateReported || p.date_reported || p.created_at?.split('T')[0];
      return reportDate === date;
    }).length;
    const solved = problems.filter(p => {
      const reportDate = p.dateReported || p.date_reported || p.created_at?.split('T')[0];
      return p.status === 'Done' && reportDate === date;
    }).length;
    return {
      date: date.slice(5), // MM-DD format
      Created: created,
      Solved: solved
    };
  });

  const getDomainStyle = (domain) => {
    const d = domain?.toLowerCase() || '';
    switch(d) {
      case 'electronics': return 'border border-blue-400 text-blue-400 bg-blue-400/10';
      case 'mechanical': return 'border border-purple-400 text-purple-400 bg-purple-400/10';
      case 'software': return 'border border-green-400 text-green-400 bg-green-400/10';
      case 'management': return 'border border-orange-400 text-orange-400 bg-orange-400/10';
      default: return 'border border-gray-400 text-gray-400 bg-gray-400/10';
    }
  };

  const getStatusStyle = (status) => {
    switch(status) {
      case 'Not Decided': return 'border border-gray-400 text-gray-400 bg-gray-400/10';
      case 'In Progress': return 'border border-blue-400 text-blue-400 bg-blue-400/10';
      case 'Done': return 'border border-green-400 text-green-400 bg-green-400/10';
      default: return 'border border-gray-400 text-gray-400 bg-gray-400/10';
    }
  };

  const formatStatus = (status) => {
    if (!status) return 'Unknown';
    return status; // Status names are already properly formatted
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="loading-dots mb-4">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <p className="text-gray-400">Loading overview...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="overview-page">
      {/* Analytics Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="glass-card p-6" data-testid="stat-total">
          <div className="flex items-center justify-between mb-4">
            <div className="text-gray-400 text-sm font-medium">Total Tasks</div>
            <TrendingUp className="text-blue-400" size={24} />
          </div>
          <div className="text-3xl font-bold">{stats.total}</div>
        </div>

        <div className="glass-card p-6" data-testid="stat-solved">
          <div className="flex items-center justify-between mb-4">
            <div className="text-gray-400 text-sm font-medium">Solved</div>
            <CheckCircle className="text-green-400" size={24} />
          </div>
          <div className="text-3xl font-bold text-green-400">{stats.solved}</div>
        </div>

        <div className="glass-card p-6" data-testid="stat-in-progress">
          <div className="flex items-center justify-between mb-4">
            <div className="text-gray-400 text-sm font-medium">In Progress</div>
            <Clock className="text-blue-400" size={24} />
          </div>
          <div className="text-3xl font-bold text-blue-400">{stats.inProgress}</div>
        </div>

        <div className="glass-card p-6" data-testid="stat-not-decided">
          <div className="flex items-center justify-between mb-4">
            <div className="text-gray-400 text-sm font-medium">Not Decided</div>
            <FileText className="text-gray-400" size={24} />
          </div>
          <div className="text-3xl font-bold text-gray-400">{stats.notDecided}</div>
        </div>
      </div>

      {/* 2-Column Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Status Distribution Floating & Glowing Pie Chart */}
          <div className="glass-card p-6">
            <h3 className="text-xl font-semibold mb-4 text-white">Status Distribution</h3>
            {/* This <style> tag defines the floating and glowing animation. We put it here so it's self-contained. */}
            <style>
              {`
                @keyframes float-glow {
                  0% {
                    transform: translateY(0px);
                    filter: drop-shadow(0 0 10px rgba(59, 130, 246, 0.4));
                  }
                  50% {
                    transform: translateY(-15px);
                    filter: drop-shadow(0 15px 15px rgba(59, 130, 246, 0.7));
                  }
                  100% {
                    transform: translateY(0px);
                    filter: drop-shadow(0 0 10px rgba(59, 130, 246, 0.4));
                  }
                }
              `}
            </style>
            {/* This div applies the animation and holds the chart. The h-[400px] is important. */}
            <div 
              className="w-full h-[400px]" 
              style={{ animation: 'float-glow 6s ease-in-out infinite' }}
            >
              {problemStatusData && problemStatusData.length > 0 ? (
                <ResponsivePie
                  data={problemStatusData}
                  margin={{ top: 20, right: 80, bottom: 80, left: 80 }}
                  innerRadius={0.5}
                  padAngle={0.7}
                  cornerRadius={3}
                  activeOuterRadiusOffset={8}
                  // --- FIX 1: This applies the colors ---
                  colors={{ datum: 'data.color' }} 
                  borderWidth={1}
                  borderColor={{ from: 'color', modifiers: [ [ 'darker', 0.2 ] ] }}
                  arcLinkLabelsSkipAngle={10}
                  arcLinkLabelsTextColor="#e5e7eb" // Light text for dark mode
                  arcLinkLabelsThickness={2}
                  arcLinkLabelsColor={{ from: 'color' }}
                  arcLabelsSkipAngle={10}
                  // --- FIX 2: This makes text on the slice dark for contrast ---
                  arcLabelsTextColor={{ from: 'color', modifiers: [ [ 'darker', 2.5 ] ] }} 
                  theme={{
                    tooltip: {
                      container: {
                        background: '#1a1f2e',
                        color: '#ffffff',
                        border: '1px solid #374151',
                      },
                    },
                    labels: {
                      text: {
                        fill: '#e5e7eb',
                        fontSize: 12,
                      },
                    },
                  }}
                  legends={[
                    {
                      anchor: 'bottom',
                      direction: 'row',
                      justify: false,
                      translateX: 0,
                      translateY: 56,
                      itemsSpacing: 0,
                      itemWidth: 100,
                      itemHeight: 18,
                      itemTextColor: '#999',
                      itemDirection: 'left-to-right',
                      itemOpacity: 1,
                      symbolSize: 18,
                      symbolShape: 'circle',
                      effects: [
                        {
                          on: 'hover',
                          style: {
                            itemTextColor: '#fff',
                          },
                        },
                      ],
                    },
                  ]}
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-gray-400">No status data available.</p>
                </div>
              )}
            </div>
          </div>

          {/* Priority Breakdown Bar Chart */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold mb-4">Priority Breakdown</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={priorityData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis type="number" stroke="#9ca3af" />
                <YAxis dataKey="name" type="category" stroke="#9ca3af" />
                <Tooltip contentStyle={{ backgroundColor: '#1a1f2e', border: '1px solid rgba(255,255,255,0.1)' }} />
                <Bar dataKey="value" radius={[0, 8, 8, 0]} animationBegin={0} animationDuration={1000}>
                  {priorityData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} className="bar-cell" style={{ animationDelay: `${index * 100}ms` }} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Recent Activity */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
            <div className="space-y-3">
              {recentProblems.map((problem) => (
                <div
                  key={problem.id}
                  onClick={() => {
                    setSelectedProblem(problem);
                    setShowDetailModal(true);
                  }}
                  className="p-4 rounded-lg bg-white/5 hover:bg-white/10 cursor-pointer transition-colors"
                  data-testid={`recent-problem-${problem.id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-medium text-sm line-clamp-1 flex-1">{problem.title.length > 50 ? problem.title.substring(0, 50) + '...' : problem.title}</h4>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getDomainStyle(problem.domain)}`}>
                      {problem.domain?.charAt(0).toUpperCase() + problem.domain?.slice(1)}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusStyle(problem.status)}`}>
                      {formatStatus(problem.status)}
                    </span>
                    <span className="text-xs text-gray-400 ml-auto">
                      {formatSimpleDate(problem.updated_at || problem.created_at)}
                    </span>
                  </div>
                </div>
              ))}
              {recentProblems.length === 0 && (
                <p className="text-gray-400 text-center py-8">No recent activity</p>
              )}
            </div>
          </div>

          {/* Domain Distribution */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold mb-4">Domain Distribution</h3>
            <div className="grid grid-cols-2 gap-4">
              {domainStats.map((item, index) => (
                <div key={index} className="p-4 rounded-lg bg-white/5">
                  <div className="text-2xl font-bold mb-1">{item.count}</div>
                  <div className="text-sm text-gray-400">{item.domain}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {problems.length > 0 ? Math.round((item.count / problems.length) * 100) : 0}%
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* 30-Day Trend Line Chart */}
      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold mb-4">30-Day Trend</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={trendData}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
            <XAxis dataKey="date" stroke="#9ca3af" />
            <YAxis stroke="#9ca3af" />
            <Tooltip contentStyle={{ backgroundColor: '#1a1f2e', border: '1px solid rgba(255,255,255,0.1)' }} />
            <Legend />
            <Line type="monotone" dataKey="Created" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} animationDuration={1000} />
            <Line type="monotone" dataKey="Solved" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} animationDuration={1000} animationBegin={200} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedProblem && (
        <ProblemDetailModal
          problem={selectedProblem}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedProblem(null);
          }}
          onEdit={() => {}}
          onDelete={async () => {
            try {
              await deleteProblem(selectedProblem.id);
              setProblems(problems.filter(p => p.id !== selectedProblem.id));
              toast.success('Problem deleted successfully');
              setShowDetailModal(false);
              setSelectedProblem(null);
            } catch (error) {
              toast.error('Failed to delete: ' + error.message);
            }
          }}
          onUpdate={async (updatedProblem) => {
            try {
              const updated = await updateProblem(updatedProblem.id, {
                status: updatedProblem.status
              });
              setProblems(problems.map(p => p.id === updated.id ? updated : p));
              setSelectedProblem(updated);
              toast.success('Status updated');
            } catch (error) {
              toast.error('Failed to update: ' + error.message);
            }
          }}
        />
      )}
    </div>
  );
};

export default Overview;
